import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { fetchData, patchData, postData } from '../api/restapi';
import ConfirmationDialog from '../popups/ConfirmationDialog';
import UpdateSessionComponent from '../tabs/UpdateSessionComponent';
import RdAutoCrashPanel from './RdAutoCrashPanel';
import { formatLocalDateTime } from '../utils/formatDateTime';
import '../assets/AllTabsCommon.css';
import '../assets/RemoteDebugSessionTabs.css';
import '../assets/NdSessionView.css';

const PAGE_SIZE = 10;
const ALL_STATUSES = ['Active', 'Scheduled', 'Completed'];

function NdRdViewSessions({ userInfo, role, userGroups = [], onStartSession, prefilledSearch = '' }) {
  const [activeSessions, setActiveSessions]       = useState([]);
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [loading, setLoading]                     = useState(true);

  // Per-tab pagination: { totalPages, totalElements }
  const [activePagination, setActivePagination]       = useState(null);
  const [scheduledPagination, setScheduledPagination] = useState(null);
  const [completedPagination, setCompletedPagination] = useState(null);

  const [statusFilter, setStatusFilter]   = useState('Active');
  const [searchTerm, setSearchTerm]       = useState(prefilledSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(prefilledSearch || '');
  const [expandedId, setExpandedId]       = useState(null);
  const [currentPage, setCurrentPage]     = useState(1);
  const [cancelTarget, setCancelTarget]   = useState(null);
  const [endTarget, setEndTarget]         = useState(null);
  const [sessionToUpdate, setSessionToUpdate] = useState(null);
  const [crashPanelId, setCrashPanelId]   = useState(null);

  const [uploadStatsMap, setUploadStatsMap] = useState({});
  const [collectorOptions, setCollectorOptions] = useState([]);
  const [sessionCollectorMap, setSessionCollectorMap] = useState({});
  const [showConfigModal, setShowConfigModal]             = useState(false);
  const [modalConfigData, setModalConfigData]             = useState(null);
  const [modalErrorMessage, setModalErrorMessage]         = useState('');
  const [currentModalSessionId, setCurrentModalSessionId] = useState('');
  const [currentModalCollectorLabel, setCurrentModalCollectorLabel] = useState('');

  // Seed search from prop
  useEffect(() => {
    if (prefilledSearch) {
      setSearchTerm(prefilledSearch);
      setDebouncedSearch(prefilledSearch);
      setCurrentPage(1);
    }
  }, [prefilledSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // 400ms search debounce — reset page on search change
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch collector options once on mount
  useEffect(() => {
    fetchData('/api/v1/diagnostic-data').then(res => {
      if (res?.statusCode === 200 && Array.isArray(res.collectorIds)) {
        setCollectorOptions(res.collectorIds.map(c => ({ value: c.id, label: c.name })));
      }
    }).catch(() => {});
  }, []);

  // Fetch counts for all three tabs once on mount (size=1 → only pagination.totalElements matters)
  const loadCounts = useCallback(async () => {
    const [activeRes, completedRes, scheduledRes] = await Promise.all([
      fetchData(`/api/v1/diagnostic-sessions?user=${userInfo}&sessionState=ACTIVE&userRole=${role}&page=1&size=1`).catch(() => null),
      fetchData(`/api/v1/diagnostic-sessions?user=${userInfo}&sessionState=INACTIVE&userRole=${role}&page=1&size=1`).catch(() => null),
      fetchData(`/api/v1/scheduled-sessions?user=${userInfo}&userRole=${role}&page=1&size=1`).catch(() => null),
    ]);
    if (activeRes?.pagination) setActivePagination(activeRes.pagination);
    if (completedRes?.pagination) setCompletedPagination(completedRes.pagination);
    if (scheduledRes?.pagination) setScheduledPagination(scheduledRes.pagination);
  }, [userInfo, role]);

  useEffect(() => { if (userInfo) loadCounts(); }, [loadCounts, userInfo]);

  // Load the selected tab's current page
  const loadSessions = useCallback(async (tab, page, search) => {
    setLoading(true);
    try {
      if (tab === 'Active') {
        let url = `/api/v1/diagnostic-sessions?user=${userInfo}&sessionState=ACTIVE&userRole=${role}&page=${page}&size=${PAGE_SIZE}`;
        if (search && search.trim()) url += `&systemId=${encodeURIComponent(search.trim())}`;
        const res = await fetchData(url).catch(() => null);
        setActiveSessions((res?.statusCode === 200 ? res.activeRemoteDebugSessions : []) || []);
        if (res?.pagination) setActivePagination(res.pagination);
      } else if (tab === 'Completed') {
        let url = `/api/v1/diagnostic-sessions?user=${userInfo}&sessionState=INACTIVE&userRole=${role}&page=${page}&size=${PAGE_SIZE}`;
        if (search && search.trim()) url += `&systemId=${encodeURIComponent(search.trim())}`;
        const res = await fetchData(url).catch(() => null);
        setCompletedSessions((res?.statusCode === 200 ? res.activeRemoteDebugSessions : []) || []);
        if (res?.pagination) setCompletedPagination(res.pagination);
      } else {
        // Scheduled — no server-side search
        const url = `/api/v1/scheduled-sessions?user=${userInfo}&userRole=${role}&page=${page}&size=${PAGE_SIZE}`;
        const res = await fetchData(url).catch(() => null);
        setScheduledSessions(
          Array.isArray(res?.scheduledRdSessions)
            ? res.scheduledRdSessions.map(s => ({ ...s, sessionId: s.scheduledSessionId }))
            : []
        );
        if (res?.pagination) setScheduledPagination(res.pagination);
      }
    } finally {
      setLoading(false);
    }
  }, [userInfo, role]);

  // Refetch when tab, page, or debounced search changes
  useEffect(() => {
    if (userInfo) loadSessions(statusFilter, currentPage, debouncedSearch);
  }, [userInfo, statusFilter, currentPage, debouncedSearch, loadSessions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowExpand = useCallback(async (session) => {
    const id = session.sessionId;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (session._status === 'Active' || session._status === 'Completed') {
      if (!uploadStatsMap[id]) {
        fetchData(`/api/v1/diagnostic-data-upload-stats?sessionId=${id}`)
          .then(res => {
            if (res?.statusCode === 200 && res.fileUploadStats) {
              setUploadStatsMap(prev => ({ ...prev, [id]: res.fileUploadStats }));
            }
          })
          .catch(() => {});
      }
    }
  }, [expandedId, uploadStatsMap]);

  const handleFetchConfigFile = async (sessionId, collectorId, collectorLabel) => {
    setModalErrorMessage('');
    setModalConfigData(null);
    setCurrentModalSessionId(sessionId);
    setCurrentModalCollectorLabel(collectorLabel);
    setShowConfigModal(true);
    try {
      const res = await fetchData(`/api/v1/rd-config-json?sessionId=${sessionId}&collectorId=${collectorId}`);
      if (res?.statusCode === 400) {
        setModalErrorMessage(res.errorMessage || 'Failed to generate config file.');
      } else {
        setModalConfigData(res);
      }
    } catch {
      setModalErrorMessage('Failed to generate config file due to network error.');
    }
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
    setModalConfigData(null);
    setModalErrorMessage('');
    setCurrentModalSessionId('');
    setCurrentModalCollectorLabel('');
    setSessionCollectorMap(prev => ({ ...prev, [currentModalSessionId]: null }));
  };

  const handleCopyToClipboard = async () => {
    if (!modalConfigData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(modalConfigData, null, 2));
      alert('JSON copied to clipboard!');
    } catch {
      alert('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    if (!modalConfigData) return;
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(modalConfigData, null, 2));
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', `${currentModalCollectorLabel}_${currentModalSessionId}.json`);
    link.click();
  };

  const handleCancelScheduled = async () => {
    if (!cancelTarget) return;
    await patchData(`/api/v1/scheduled-sessions/${cancelTarget.scheduledSessionId || cancelTarget.sessionId}/cancel`, {}).catch(() => {});
    setCancelTarget(null);
    await loadSessions(statusFilter, currentPage, debouncedSearch);
    await loadCounts();
  };

  const handleEndConfirm = async () => {
    if (!endTarget) return;
    await postData('/api/v1/configure-diagnostics', {
      systemId: endTarget.systemId,
      sessionId: endTarget.sessionId,
      sessionType: 'WBC_END_SESSION',
      user: userInfo,
    }).catch(() => {});
    setEndTarget(null);
    await loadSessions(statusFilter, currentPage, debouncedSearch);
    await loadCounts();
  };

  const handleUpdate = async (session) => {
    const latest = await fetchData(`/api/v1/latest-collector-status?sessionId=${session.sessionId}`).catch(() => null);
    const updatedSessionData = { ...session };
    if (latest?.latestCollectorStatus && Object.keys(latest.latestCollectorStatus).length > 0) {
      updatedSessionData.currentDiagnosticData = Object.entries(latest.latestCollectorStatus).map(([collectorId, commandName]) => ({
        collectorId: parseInt(collectorId),
        mqttCommandName: commandName,
      }));
    } else {
      updatedSessionData.currentDiagnosticData = [];
    }
    setSessionToUpdate(updatedSessionData);
  };

  const handleFilterClick = (st) => {
    setStatusFilter(st);
    setCurrentPage(1);
    setExpandedId(null);
  };

  const counts = {
    Active:    activePagination?.totalElements ?? activeSessions.length,
    Scheduled: scheduledPagination?.totalElements ?? scheduledSessions.length,
    Completed: completedPagination?.totalElements ?? completedSessions.length,
  };

  const totalPages = (
    statusFilter === 'Active' ? activePagination?.totalPages :
    statusFilter === 'Completed' ? completedPagination?.totalPages :
    scheduledPagination?.totalPages
  ) ?? 1;

  const currentSessions = statusFilter === 'Active' ? activeSessions
    : statusFilter === 'Completed' ? completedSessions
    : scheduledSessions;

  // Client-side search filter for Scheduled (no server-side search on that endpoint)
  const pageItems = (statusFilter === 'Scheduled' && searchTerm)
    ? currentSessions.filter(s => (s.systemId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.sessionId || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : currentSessions;

  // Tag each row with _status for the detail panel
  const taggedItems = pageItems.map(s => ({ ...s, _status: statusFilter }));

  const renderPageNumbers = () => {
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => setCurrentPage(i)} className={currentPage === i ? 'active' : ''}>
          {i}
        </button>
      );
    }
    return (
      <>
        <button onClick={() => setCurrentPage(1)}>&lt;&lt;</button>
        {pages}
        <button onClick={() => setCurrentPage(totalPages)}>&gt;&gt;</button>
      </>
    );
  };

  const STATUS_META = {
    Active:    { badge: 'nd-sv-badge nd-sv-badge--active',    icon: '●'  },
    Scheduled: { badge: 'nd-sv-badge nd-sv-badge--scheduled', icon: '⏳' },
    Completed: { badge: 'nd-sv-badge nd-sv-badge--completed', icon: '✓'  },
  };

  if (sessionToUpdate) {
    return (
      <div className="nd-rd-form-wrap">
        <UpdateSessionComponent
          session={sessionToUpdate}
          userInfo={userInfo}
          collectorNames={{}}
          onGoBack={() => { setSessionToUpdate(null); loadSessions(statusFilter, currentPage, debouncedSearch); }}
        />
      </div>
    );
  }

  return (
    <div className="nd-sv-root">

      {/* ── Stat cards ── */}
      <div className="nd-sv-stats">
        {['Active', 'Scheduled', 'Completed'].map(st => (
          <div
            key={st}
            className={`nd-sv-stat-card${statusFilter === st ? ' nd-sv-stat-card--active' : ''}`}
            onClick={() => handleFilterClick(st)}
          >
            <div className="nd-sv-stat-n">{loading && statusFilter === st ? '…' : counts[st]}</div>
            <div className="nd-sv-stat-label">{STATUS_META[st].icon} {st}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div className="nd-sv-filter-bar">
        <div className="nd-sv-status-pills">
          {ALL_STATUSES.map(st => (
            <button
              key={st}
              className={`nd-sv-pill${statusFilter === st ? ' nd-sv-pill--active' : ''}`}
              onClick={() => handleFilterClick(st)}
            >
              {st}
            </button>
          ))}
        </div>
        <div className="nd-sv-search">
          <span className="nd-search-icon">🔍</span>
          <input
            className="nd-search-input"
            type="text"
            placeholder={statusFilter === 'Scheduled' ? 'Search by System or Session ID…' : 'Search by System ID…'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="nd-search-clear" onClick={() => { setSearchTerm(''); setDebouncedSearch(''); setCurrentPage(1); }}>✕</button>
          )}
        </div>
        <button className="nd-sv-start-btn" onClick={onStartSession}>+ Start Session</button>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="nd-no-data">Loading sessions…</div>
      ) : taggedItems.length === 0 ? (
        <div className="nd-sv-empty">
          <div className="nd-sv-empty-icon">🔧</div>
          <div className="nd-sv-empty-title">No {statusFilter.toLowerCase()} RD sessions found</div>
          <div className="nd-sv-empty-hint">
            {statusFilter === 'Active' ? 'Start a new RD session to see it here.' :
             statusFilter === 'Scheduled' ? 'Schedule a session from the Start tab.' :
             'Completed sessions will appear here once they finish.'}
          </div>
        </div>
      ) : (
        <>
          <div className="nd-sv-table-container"><div className="nd-sv-table-wrapper">
            <table className="nd-sv-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>System ID</th>
                  <th style={{ width: '32%' }}>Session ID</th>
                  <th style={{ width: '22%' }}>Start / Scheduled Time</th>
                  <th style={{ width: '12%' }}>Duration</th>
                  <th style={{ width: '12%' }}></th>
                </tr>
              </thead>
              <tbody>
                {taggedItems.map(session => (
                  <React.Fragment key={session.sessionId}>
                    <tr
                      className={`nd-sv-row${expandedId === session.sessionId ? ' expanded-row' : ''}`}
                      data-status={session._status}
                      onClick={() => handleRowExpand(session)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="nd-system-id" title={session.systemId}>{session.systemId}</td>
                      <td title={session.sessionId}>
                        <span className="clickable-session-id" style={{ color: '#1a4fba', textDecoration: 'underline' }}>
                          {session.sessionId}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#475569' }}>
                        {session._status === 'Scheduled'
                          ? formatLocalDateTime(session.scheduledTime)
                          : formatLocalDateTime(session.createdOn || session.startTime)}
                      </td>
                      <td>{session.sessionTimeInMins ? `${session.sessionTimeInMins} min` : '—'}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {session._status === 'Scheduled' && (
                          <button
                            className="nd-sv-row-action nd-sv-row-action--cancel"
                            onClick={() => setCancelTarget(session)}
                          >
                            Cancel
                          </button>
                        )}
                        {session._status === 'Active' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="nd-sv-row-action nd-sv-row-action--update"
                              onClick={() => handleUpdate(session)}
                            >
                              Update
                            </button>
                            <button
                              className="nd-sv-row-action nd-sv-row-action--stop"
                              onClick={() => setEndTarget(endTarget?.sessionId === session.sessionId ? null : session)}
                            >
                              {endTarget?.sessionId === session.sessionId ? 'Cancel' : 'End'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>

                    {endTarget?.sessionId === session.sessionId && (
                      <tr className="nd-sv-confirm-row">
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div className="nd-sv-confirm-content">
                            <span className="nd-sv-confirm-text">
                              ⚠️ Are you sure you want to end session <strong>{session.sessionId}</strong>?
                            </span>
                            <div className="nd-sv-confirm-actions">
                              <button className="nd-sv-confirm-cancel" onClick={() => setEndTarget(null)}>
                                Cancel
                              </button>
                              <button className="nd-sv-confirm-confirm" onClick={handleEndConfirm}>
                                Confirm End Session
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                    {expandedId === session.sessionId && (
                      <tr className="expanded-details-row">
                        <td colSpan={5}>
                          <div className="expanded-row-content nd-sv-expanded">
                            <RdSessionDetail
                              session={session}
                              userGroups={userGroups}
                              crashPanelId={crashPanelId}
                              setCrashPanelId={setCrashPanelId}
                              uploadStats={uploadStatsMap[session.sessionId] || null}
                              collectorOptions={collectorOptions}
                              selectedCollector={sessionCollectorMap[session.sessionId] || null}
                              onCollectorChange={(opt) => {
                                setSessionCollectorMap(prev => ({ ...prev, [session.sessionId]: opt }));
                                handleFetchConfigFile(session.sessionId, opt.value, opt.label);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div></div>
          {totalPages > 1 && (
            <div className="pagination-controls">{renderPageNumbers()}</div>
          )}
        </>
      )}

      {cancelTarget && (
        <ConfirmationDialog
          message={`Cancel the scheduled RD session for device ${cancelTarget.systemId}?`}
          onConfirm={handleCancelScheduled}
          onCancel={() => setCancelTarget(null)}
        />
      )}

      {showConfigModal && (
        <div className="nd-sv-review-overlay" onClick={e => { if (e.target === e.currentTarget) closeConfigModal(); }}>
          <div className="nd-sv-config-modal">
            <div className="nd-sv-config-modal-header">
              <div>
                <h3>Config File</h3>
                <p>{currentModalCollectorLabel} · {currentModalSessionId}</p>
              </div>
              <button className="nd-sv-review-close" onClick={closeConfigModal}>✕</button>
            </div>
            <div className="nd-sv-config-modal-body">
              {modalErrorMessage ? (
                <div className="nd-sv-error-banner" style={{ margin: 0 }}>
                  <span>{modalErrorMessage}</span>
                  <button className="nd-sv-error-close" onClick={() => setModalErrorMessage('')}>✕</button>
                </div>
              ) : modalConfigData ? (
                <pre className="nd-sv-config-json">{JSON.stringify(modalConfigData, null, 2)}</pre>
              ) : (
                <span className="nd-sv-loading-hint">Loading configuration…</span>
              )}
            </div>
            <div className="nd-sv-review-modal-footer">
              <button className="nd-sv-review-cancel" onClick={closeConfigModal}>Close</button>
              {modalConfigData && (
                <>
                  <button className="nd-sv-review-cancel" onClick={handleCopyToClipboard}>Copy to Clipboard</button>
                  <button className="nd-sv-review-apply" onClick={handleDownload}>Download File</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadStatsPanel({ uploadStats }) {
  if (!uploadStats) return <div className="nd-sv-stats-panel"><span className="nd-sv-loading-hint">Loading…</span></div>;
  return (
    <div className="nd-sv-stats-panel">
      <div className="nd-sv-stats-panel-counts">
        <div className="nd-sv-stats-panel-item nd-sv-stats-panel-item--files">
          <span className="nd-sv-stats-panel-value">{uploadStats.filesUploadedCount ?? 0}</span>
          <span className="nd-sv-stats-panel-label">Files Uploaded</span>
        </div>
        {uploadStats.uploadBaseUrl && (
          <div className="nd-sv-stats-panel-item nd-sv-stats-panel-item--url">
            <span className="nd-sv-stats-panel-url-value">{uploadStats.uploadBaseUrl}</span>
            <span className="nd-sv-stats-panel-label">Upload Base URL</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Parse a scheduled session's wbcCollectorConfigs JSON → [{collectorId, commandName}]
function ScheduledConfigTable({ session, collectorNameMap }) {
  let rows = [];
  if (session.wbcCollectorConfigs) {
    try {
      const configs = JSON.parse(session.wbcCollectorConfigs);
      if (Array.isArray(configs)) {
        rows = configs.map(c => ({ collectorId: c.collectorId, commandName: c.commandName }));
      }
    } catch { /* ignore malformed JSON */ }
  }
  return (
    <div className="nd-sv-config-table-wrap">
      <div className="nd-sv-config-table-title">Configured Diagnostic Data</div>
      {rows.length === 0 ? (
        <span className="rt-text-muted">No diagnostic data configured.</span>
      ) : (
        <table className="nd-sv-config-table">
          <thead>
            <tr><th>Diagnostic Data</th><th>Command</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{collectorNameMap[r.collectorId] || r.collectorId}</td>
                <td>{r.commandName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// One collapsible rules cell (ODL context collection rules under a command)
function RulesCell({ command }) {
  const [open, setOpen] = useState(false);
  const hasRules = command?.metadata?.odlMode === 'ODL_CONTEXT_COLLECTION' &&
    Array.isArray(command?.metadata?.rules) && command.metadata.rules.length > 0;
  if (!command) return <>—</>;
  return (
    <div>
      <span className="nd-sv-cmd-name">{command.command}</span>
      {hasRules ? (
        <div className="nd-sv-rules-wrap">
          <button className="nd-sv-rules-toggle" onClick={() => setOpen(v => !v)}>
            {command.metadata.rules.length} Rule{command.metadata.rules.length !== 1 ? 's' : ''} {open ? '▲' : '▼'}
          </button>
          {open && (
            <div className="nd-sv-rules-list">
              {command.metadata.rules.map((rule, ri) => (
                <div key={ri} className="nd-sv-rule-chip">
                  <span className="nd-sv-rule-label">{rule.ruleName || `Rule ${rule.ruleId}`}</span>
                  {rule.logMaskDescription && <span className="nd-sv-rule-mask">· {rule.logMaskDescription}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : command.metadata?.odlMode === 'ODL_CONTEXT_COLLECTION' ? (
        <span className="rt-text-muted"> (no rules)</span>
      ) : command.logMask ? (
        <span className="nd-sv-cmd-logmask"> · Log Mask: {command.logMask}</span>
      ) : null}
    </div>
  );
}

// Active/Completed collector command table with per-update columns (from collectorExecutionStatuses)
function CollectorConfigTable({ statuses, collectorNameMap }) {
  // Group by collector; bucket commands by collectorUpdateCounter (0 = Created, 1+ = Nth Update)
  const grouped = {};
  let maxCounter = 0;
  statuses.forEach(d => {
    const name = collectorNameMap[d.collectorId] || d.collectorName || d.collectorId;
    if (!grouped[name]) grouped[name] = { created: null };
    const cell = {
      command: d.mqttCommandName,
      logMask: d.logMaskDescription || null,
      metadata: d.metadata || null,
    };
    const counter = d.collectorUpdateCounter || 0;
    if (counter === 0) grouped[name].created = cell;
    else { grouped[name][`update${counter}`] = cell; if (counter > maxCounter) maxCounter = counter; }
  });

  const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

  return (
    <div className="nd-sv-config-table-wrap">
      <div className="nd-sv-config-table-title">Diagnostic Config</div>
      <div className="nd-sv-config-table-scroll">
        <table className="nd-sv-config-table">
          <thead>
            <tr>
              <th>Diagnostic Data</th>
              <th>Command – Created</th>
              {Array.from({ length: maxCounter }).map((_, i) => (
                <th key={i}>Command – {ORDINALS[i] || `${i + 1}th`} Update</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([name, cmds]) => (
              <tr key={name}>
                <td>{name}</td>
                <td><RulesCell command={cmds.created} /></td>
                {Array.from({ length: maxCounter }).map((_, i) => (
                  <td key={i}><RulesCell command={cmds[`update${i + 1}`]} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RdSessionDetail({ session, userGroups, crashPanelId, setCrashPanelId, uploadStats, collectorOptions, selectedCollector, onCollectorChange }) {
  const showCrash = crashPanelId === session.sessionId;
  const [showConfigChooser, setShowConfigChooser] = useState(false);
  const [showUploadStats, setShowUploadStats] = useState(false);
  const canShowExtras = session._status === 'Active' || session._status === 'Completed';

  // collectorId → human name, from the options already loaded for the config dropdown
  const collectorNameMap = {};
  (collectorOptions || []).forEach(o => { collectorNameMap[o.value] = o.label; });

  const terminationReason = session.reason || session.termination || session.terminationReason || null;

  return (
    <div>
      <div className="nd-sv-detail-grid">
        <div className="nd-sv-detail-item"><span>Session ID</span><strong>{session.sessionId}</strong></div>
        <div className="nd-sv-detail-item"><span>System ID</span><strong>{session.systemId}</strong></div>
        <div className="nd-sv-detail-item"><span>Status</span><strong>{session._status}</strong></div>
        {session._status === 'Scheduled' ? (
          <>
            <div className="nd-sv-detail-item"><span>Scheduled Time</span><strong>{formatLocalDateTime(session.scheduledTime)}</strong></div>
            <div className="nd-sv-detail-item"><span>Scheduling Mode</span><strong>{session.schedulingMode || '—'}</strong></div>
            <div className="nd-sv-detail-item"><span>Scheduled By</span><strong>{session.scheduledBy || '—'}</strong></div>
            <div className="nd-sv-detail-item"><span>Duration</span><strong>{session.sessionTimeInMins ? `${session.sessionTimeInMins} min` : '—'}</strong></div>
          </>
        ) : (
          <>
            <div className="nd-sv-detail-item"><span>Created On</span><strong>{formatLocalDateTime(session.createdOn)}</strong></div>
            <div className="nd-sv-detail-item">
              <span>{session._status === 'Completed' ? 'Open Ended' : 'Duration'}</span>
              <strong>
                {session._status === 'Completed'
                  ? (session.isOpenEnded ? 'Yes' : 'No')
                  : (session.sessionTimeInMins ? `${session.sessionTimeInMins} min` : '—')}
              </strong>
            </div>
            <div className="nd-sv-detail-item"><span>Created By</span><strong>{session.user || '—'}</strong></div>
            {session.sessionEndedAt && (
              <div className="nd-sv-detail-item"><span>Ended At</span><strong>{formatLocalDateTime(session.sessionEndedAt)}</strong></div>
            )}
            {session._status === 'Completed' && (
              <div className="nd-sv-detail-item"><span>Termination Reason</span><strong>{terminationReason || 'Session ended as scheduled'}</strong></div>
            )}
          </>
        )}
      </div>

      {/* Scheduled — configured diagnostic data (parsed from wbcCollectorConfigs) */}
      {session._status === 'Scheduled' && (
        <ScheduledConfigTable session={session} collectorNameMap={collectorNameMap} />
      )}

      {/* Active / Completed — collector command + update history */}
      {(session._status === 'Active' || session._status === 'Completed') &&
        session.collectorExecutionStatuses?.length > 0 && (
        <CollectorConfigTable
          statuses={session.collectorExecutionStatuses}
          collectorNameMap={collectorNameMap}
        />
      )}

      {/* Action buttons — Active and Completed only */}
      {canShowExtras && (
        <div className="nd-sv-action-links">
          <button
            className={`nd-sv-action-link${showUploadStats ? ' nd-sv-action-link--active' : ''}`}
            onClick={() => setShowUploadStats(v => !v)}
          >
            📊 {showUploadStats ? 'Hide Upload Statistics' : 'Upload Statistics'}
          </button>
          <button
            className={`nd-sv-action-link${showCrash ? ' nd-sv-action-link--active' : ''}`}
            onClick={() => setCrashPanelId(showCrash ? null : session.sessionId)}
          >
            📋 {showCrash ? 'Hide Recommendations' : 'View Recommendations'}
          </button>
          <button
            className={`nd-sv-action-link${showConfigChooser ? ' nd-sv-action-link--active' : ''}`}
            onClick={() => setShowConfigChooser(v => !v)}
          >
            📄 {showConfigChooser ? 'Hide Config File' : 'Config File'}
          </button>
        </div>
      )}

      {/* Upload Statistics panel */}
      {canShowExtras && showUploadStats && (
        <UploadStatsPanel uploadStats={uploadStats} />
      )}

      {/* Config File collector picker */}
      {canShowExtras && showConfigChooser && (
        <div className="nd-sv-config-chooser" onClick={e => e.stopPropagation()}>
          <p className="nd-sv-config-chooser-hint">Select a collector to generate its config file:</p>
          <Select
            options={collectorOptions}
            value={selectedCollector}
            onChange={onCollectorChange}
            placeholder="Select Diagnostic Data…"
            classNamePrefix="nd-select"
          />
        </div>
      )}

      {/* Recommendations panel */}
      {showCrash && (
        <div style={{ marginTop: 12 }}>
          <RdAutoCrashPanel
            sessionId={session.sessionId}
            userGroups={userGroups}
            onClose={() => setCrashPanelId(null)}
          />
        </div>
      )}
    </div>
  );
}

export default NdRdViewSessions;
